class ProductSlider {
  constructor(wrapId, options) {
    this.wrap = document.getElementById(wrapId);
    if (!this.wrap) return;

    this.options = Object.assign(
      { autoplay: true, autoplaySpeed: 4000, pauseOnHover: true, loop: true },
      options
    );

    this.track = this.wrap.querySelector('[data-slider-track]');
    this.items = Array.from(this.wrap.querySelectorAll('.product-slider-item'));
    this.prevBtn = this.wrap.querySelector('[data-slider-prev]');
    this.nextBtn = this.wrap.querySelector('[data-slider-next]');
    this.dotsWrap = this.wrap.parentElement.querySelector('[data-slider-dots]');

    if (!this.track || this.items.length === 0) return;

    this.timer = null;

    this.bindArrows();
    this.bindDragScroll();
    this.buildDots();
    this.bindScrollSync();

    if (this.options.pauseOnHover) {
      this.wrap.addEventListener('mouseenter', () => this.stopAutoplay());
      this.wrap.addEventListener('mouseleave', () => this.startAutoplay());
    }

    // Recompute dot count/positions on resize, since items-per-view changes
    // at each breakpoint and the number of "pages" changes with it.
    window.addEventListener('resize', () => this.buildDots());

    if (this.options.autoplay) this.startAutoplay();
  }

  getItemStep() {
    // Distance to advance one item, including the gap, read from actual
    // rendered layout rather than recalculating the CSS math in JS.
    if (this.items.length < 2) return this.items[0] ? this.items[0].offsetWidth : 0;
    return this.items[1].offsetLeft - this.items[0].offsetLeft;
  }

  scrollByItems(count) {
    const step = this.getItemStep();
    this.track.scrollBy({ left: step * count, behavior: 'smooth' });
  }

  bindArrows() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.scrollByItems(-1);
        this.restartAutoplay();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.advance();
        this.restartAutoplay();
      });
    }
  }

  advance() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atEnd = this.track.scrollLeft >= maxScroll - 4;

    if (atEnd) {
      if (this.options.loop) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
      }
      return;
    }
    this.scrollByItems(1);
  }

  // ---------- Autoplay ----------
  startAutoplay() {
    this.stopAutoplay();
    if (!this.options.autoplay) return;
    this.timer = setInterval(() => this.advance(), this.options.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restartAutoplay() {
    if (this.options.autoplay) this.startAutoplay();
  }

  // ---------- Drag to scroll (mouse) ----------
  bindDragScroll() {
    let isDown = false;
    let startX = 0;
    let scrollStart = 0;

    const start = (pageX) => {
      isDown = true;
      startX = pageX - this.track.offsetLeft;
      scrollStart = this.track.scrollLeft;
      this.track.classList.add('is-dragging');
      this.stopAutoplay();
    };

    const move = (pageX) => {
      if (!isDown) return;
      const x = pageX - this.track.offsetLeft;
      const walk = x - startX;
      this.track.scrollLeft = scrollStart - walk;
    };

    const end = () => {
      if (!isDown) return;
      isDown = false;
      this.track.classList.remove('is-dragging');
      this.restartAutoplay();
    };

    this.track.addEventListener('mousedown', (e) => {
      start(e.pageX);
      e.preventDefault();
    });
    window.addEventListener('mousemove', (e) => move(e.pageX));
    window.addEventListener('mouseup', end);
    this.track.addEventListener('mouseleave', () => {
      if (isDown) end();
    });
  }

  // ---------- Dot pagination ----------
  buildDots() {
    if (!this.dotsWrap) return;

    const itemWidth = this.getItemStep();
    if (!itemWidth) return;

    const visibleCount = Math.max(1, Math.round(this.track.clientWidth / itemWidth));
    const pageCount = Math.max(1, Math.ceil(this.items.length / visibleCount));

    this.dotsWrap.innerHTML = '';
    this.dots = [];

    for (let i = 0; i < pageCount; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'product-slider__dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', `Go to page ${i + 1}`);
      dot.addEventListener('click', () => {
        this.track.scrollTo({ left: itemWidth * visibleCount * i, behavior: 'smooth' });
        this.restartAutoplay();
      });
      this.dotsWrap.appendChild(dot);
      this.dots.push(dot);
    }

    this.visibleCount = visibleCount;
  }

  bindScrollSync() {
    let scrollTimeout;
    this.track.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => this.updateActiveDot(), 80);
    });
  }

  updateActiveDot() {
    if (!this.dots || this.dots.length === 0) return;
    const itemWidth = this.getItemStep();
    if (!itemWidth || !this.visibleCount) return;

    const page = Math.round(this.track.scrollLeft / (itemWidth * this.visibleCount));
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === page));
  }
}

window.ProductSlider = ProductSlider;
